'use client';

import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import {
  usePatchJobMutation,
  useTransitionJobMutation,
} from '@/features/jobs/api/jobsApi';
import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import { uiConfig } from '@web/constants';
import {
  CloseIcon,
  DataBadge,
  DatePicker,
  MainButton,
  Modal,
  SecondaryButton,
} from '@web/ui';
import { formatDate, formatHours, getErrorMessage, notify } from '@web/utils';

import styles from './JobActivateModal.module.scss';

import type { JobSummaryDraft } from './types';
import type { Dayjs } from 'dayjs';

type JobActivateModalProps = {
  open: boolean;
  onClose: () => void;
  summary: JobSummaryDraft;
  totalHours: number;
  durationDays: number | null;
  deliverablesCount: number;
};

function toIsoDate(value: Dayjs | null) {
  return value ? value.format('YYYY-MM-DD') : '';
}

function formatJobNumber(jobNumber: number | null) {
  if (!jobNumber || jobNumber <= 0) return 'JOB';
  return `JOB ${String(jobNumber).padStart(2, '0')}`;
}

export function JobActivateModal({
  open,
  onClose,
  summary,
  totalHours,
  durationDays,
  deliverablesCount,
}: JobActivateModalProps) {
  const router = useRouter();
  const workspaceRoutes = useWorkspaceRoutes();
  const [patchJob] = usePatchJobMutation();
  const [transitionJob] = useTransitionJobMutation();

  const originalStartDateIso =
    summary.startDate?.startOf('day').format('YYYY-MM-DD') ?? '';
  const originalStartDate = useMemo(
    () => (originalStartDateIso ? dayjs(originalStartDateIso) : null),
    [originalStartDateIso],
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(
    originalStartDate ?? dayjs().startOf('day'),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const nextStartDate = originalStartDateIso
      ? dayjs(originalStartDateIso)
      : dayjs().startOf('day');
    setStartDate(nextStartDate);
    setIsSubmitting(false);
  }, [open, originalStartDateIso]);

  const jobId = summary.id ?? '';
  const isProject = summary.jobKind === 'project';
  const isStartDateInvalid = !startDate;
  const canActivate = !isStartDateInvalid && !isSubmitting && Boolean(jobId);

  const startDateError = isStartDateInvalid ? 'Start date is required.' : '';
  const startDateHint = startDate
    ? `Start date will be ${formatDate(startDate.toDate(), uiConfig.dateFormats.date)}.`
    : '';

  const durationValue = isProject
    ? durationDays && durationDays > 0
      ? `${durationDays} d`
      : 'Not set'
    : 'Not applicable';

  const durationDescription = isProject
    ? summary.endDate
      ? `Ends ${formatDate(summary.endDate.toDate(), uiConfig.dateFormats.date)}`
      : 'End date not set.'
    : undefined;

  const executeActivation = async () => {
    if (!startDate || !jobId) return;

    setIsSubmitting(true);
    try {
      await patchJob({
        jobId,
        payload: {
          start_date: toIsoDate(startDate),
        },
      }).unwrap();

      await transitionJob({ jobId, to: 'active' }).unwrap();

      notify({ type: 'success', message: 'Job activated successfully.' });
      onClose();
      router.push(workspaceRoutes.jobs.detailTasks(jobId));
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onActivateClick = () => {
    if (!canActivate) return;
    void executeActivation();
  };

  return (
    <>
      <Modal open={open} onClose={onClose} width="min(900px, 95vw)">
        <section className={styles.jobActivateModal}>
          <header className={styles.jobActivateModal__header}>
            <div>
              <h2 className={styles.jobActivateModal__title}>
                Activation Review
              </h2>
              <p className={styles.jobActivateModal__subtitle}>
                Review the schedule and confirm activation details before this
                job goes live.
              </p>
            </div>
            <div className={styles.jobActivateModal__close}>
              <SecondaryButton
                variant="medium"
                ariaLabel="Close activation review"
                onClick={onClose}
              >
                <CloseIcon />
              </SecondaryButton>
            </div>
          </header>

          <div className={styles.jobActivateModal__summaryCard}>
            <DataBadge
              title={formatJobNumber(summary.jobNumber)}
              value={summary.name || 'Untitled Job'}
              description="Billing begins when this job is activated."
            />
          </div>

          <div className={styles.jobActivateModal__startDateBlock}>
            <p className={styles.jobActivateModal__sectionLabel}>Start date</p>
            <DatePicker
              name="job-activate-start-date"
              value={startDate}
              format="MM/DD/YYYY"
              onChange={setStartDate}
              error={startDateError}
              helperText={!startDateError ? startDateHint : undefined}
            />
          </div>

          <div className={styles.jobActivateModal__planSummary}>
            <p className={styles.jobActivateModal__sectionLabel}>
              Plan summary
            </p>
            <div className={styles.jobActivateModal__planGrid}>
              <DataBadge title="Total hours" value={formatHours(totalHours)} />
              <DataBadge
                title="Duration"
                value={durationValue}
                description={durationDescription}
              />
              <DataBadge title="Deliverables" value={deliverablesCount} />
            </div>
          </div>

          <footer className={styles.jobActivateModal__footer}>
            <SecondaryButton ariaLabel="Cancel activation" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <MainButton
              ariaLabel="Activate Job"
              onClick={onActivateClick}
              disabled={!canActivate}
              isLoading={isSubmitting}
            >
              Activate Job
            </MainButton>
          </footer>
        </section>
      </Modal>
    </>
  );
}
